/**
 * Tactical HUD — 글로브 위에 얹히는 군용 헤드업 디스플레이.
 *
 * God's Eye View (MIT) 의 cockpit HUD 구성을 참고해 React/SVG 로 재작성.
 * 전부 pointer-events-none 이라 지도 조작을 막지 않는다.
 */
import { type FC, useEffect, useMemo, useState } from 'react';
import { useAppStore } from '@/stores/app-store';
import { SENSOR_PRESETS, sensorAccent } from '@/components/map/sensor-optics';
import { getGlobeBasemap } from '@/config/map-styles';

// ---------------------------------------------------------------------------
// 포맷 헬퍼
// ---------------------------------------------------------------------------

/** 십진 도 → 도분초 (항공/군용 표기) */
function toDMS(value: number, axis: 'lat' | 'lon'): string {
  const hemi = axis === 'lat' ? (value >= 0 ? 'N' : 'S') : (value >= 0 ? 'E' : 'W');
  const abs = Math.abs(value);
  const deg = Math.floor(abs);
  const minFloat = (abs - deg) * 60;
  const min = Math.floor(minFloat);
  const sec = (minFloat - min) * 60;
  const pad = axis === 'lat' ? 2 : 3;
  return `${String(deg).padStart(pad, '0')}°${String(min).padStart(2, '0')}'${sec.toFixed(1).padStart(4, '0')}"${hemi}`;
}

/** 줌 → 카메라 고도(m). deck.gl 기본 fovy 기준 근사값. */
function zoomToAltitude(zoom: number, lat: number, viewportHeight: number): number {
  const metersPerPixel = (156543.03392 * Math.cos((lat * Math.PI) / 180)) / Math.pow(2, zoom);
  return Math.max(0, metersPerPixel * viewportHeight * 1.5);
}

function formatAltitude(m: number): { value: string; unit: string } {
  if (m >= 1000) return { value: (m / 1000).toFixed(m >= 100000 ? 0 : 1), unit: 'KM' };
  return { value: m.toFixed(0), unit: 'M' };
}

const CARDINALS: Record<number, string> = {
  0: 'N', 45: 'NE', 90: 'E', 135: 'SE', 180: 'S', 225: 'SW', 270: 'W', 315: 'NW',
};

// ---------------------------------------------------------------------------
// 베어링 테이프 (상단 컴퍼스 눈금자)
// ---------------------------------------------------------------------------

const BearingTape: FC<{ bearing: number; accent: string }> = ({ bearing, accent }) => {
  const span = 90;          // 화면에 보이는 각도 범위
  const width = 340;
  const pxPerDeg = width / span;

  const ticks = useMemo(() => {
    const out: { deg: number; x: number; major: boolean; label?: string }[] = [];
    const start = Math.floor((bearing - span / 2) / 5) * 5;
    for (let d = start; d <= bearing + span / 2; d += 5) {
      const norm = ((d % 360) + 360) % 360;
      const x = width / 2 + (d - bearing) * pxPerDeg;
      if (x < -20 || x > width + 20) continue;
      const major = norm % 45 === 0;
      out.push({ deg: norm, x, major, label: major ? (CARDINALS[norm] ?? String(norm)) : undefined });
    }
    return out;
  }, [bearing, pxPerDeg]);

  return (
    <svg width={width} height={30} className="overflow-visible">
      <line x1={0} y1={22} x2={width} y2={22} stroke={accent} strokeWidth={1} strokeOpacity={0.25} />
      {ticks.map((t, i) => (
        <g key={i}>
          <line
            x1={t.x} y1={t.major ? 12 : 17} x2={t.x} y2={22}
            stroke={accent} strokeWidth={t.major ? 1.4 : 1} strokeOpacity={t.major ? 0.8 : 0.35}
          />
          {t.label && (
            <text
              x={t.x} y={8} textAnchor="middle"
              className="font-mono"
              fontSize={9} fill={accent} fillOpacity={0.9} letterSpacing={0.5}
            >
              {t.label}
            </text>
          )}
        </g>
      ))}
      {/* 중앙 인덱스 마커 */}
      <path d={`M ${width / 2} 26 l -5 7 l 10 0 z`} fill={accent} fillOpacity={0.95} />
    </svg>
  );
};

// ---------------------------------------------------------------------------
// 중앙 레티클
// ---------------------------------------------------------------------------

const Reticle: FC<{ accent: string }> = ({ accent }) => (
  <svg width={150} height={150} className="overflow-visible">
    <g transform="translate(75 75)">
      <circle r={34} fill="none" stroke={accent} strokeWidth={1} strokeOpacity={0.3} />
      <circle r={2} fill={accent} fillOpacity={0.85} />
      {/* 4방향 크로스헤어 (중앙 갭) */}
      {[0, 90, 180, 270].map((a) => (
        <g key={a} transform={`rotate(${a})`}>
          <line x1={0} y1={-12} x2={0} y2={-30} stroke={accent} strokeWidth={1.2} strokeOpacity={0.7} />
          <line x1={-4} y1={-42} x2={4} y2={-42} stroke={accent} strokeWidth={1} strokeOpacity={0.35} />
        </g>
      ))}
      {/* 각도 눈금 */}
      {Array.from({ length: 24 }, (_, i) => i * 15).map((a) => (
        <line
          key={a}
          transform={`rotate(${a})`}
          x1={0} y1={-34} x2={0} y2={a % 45 === 0 ? -40 : -37}
          stroke={accent} strokeWidth={0.8} strokeOpacity={0.3}
        />
      ))}
    </g>
  </svg>
);

// ---------------------------------------------------------------------------
// 라벨 블록
// ---------------------------------------------------------------------------

const Readout: FC<{ label: string; value: string; accent: string; dim?: boolean }> = ({
  label, value, accent, dim,
}) => (
  <div className="flex items-baseline gap-1.5 leading-none">
    <span className="font-mono text-[8px] tracking-[0.18em] text-slate-500 uppercase">{label}</span>
    <span
      className="font-mono text-[11px] tabular-nums tracking-tight"
      style={{ color: dim ? 'rgb(148 163 184)' : accent }}
    >
      {value}
    </span>
  </div>
);

// ---------------------------------------------------------------------------
// TacticalHUD
// ---------------------------------------------------------------------------

export const TacticalHUD: FC = () => {
  const hudEnabled   = useAppStore((s) => s.hudEnabled);
  const viewState    = useAppStore((s) => s.viewState);
  const sensorMode   = useAppStore((s) => s.sensorMode);
  const globeBasemap = useAppStore((s) => s.globeBasemap);
  const cursorGeo    = useAppStore((s) => s.cursorGeo);
  const aircraft     = useAppStore((s) => s.aircraft);
  const militaryAir  = useAppStore((s) => s.militaryAircraft);
  const ships        = useAppStore((s) => s.ships);
  const events       = useAppStore((s) => s.events);
  const crisisAlerts = useAppStore((s) => s.crisisAlerts);

  const accent = sensorAccent(sensorMode);
  const sensor = SENSOR_PRESETS.find((p) => p.id === sensorMode) ?? SENSOR_PRESETS[0];
  const basemap = getGlobeBasemap(globeBasemap);

  // Zulu 시계 — 1초 틱
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const [vh, setVh] = useState(() => (typeof window === 'undefined' ? 900 : window.innerHeight));
  useEffect(() => {
    const onResize = () => setVh(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  if (!hudEnabled) return null;

  const lat = viewState.latitude ?? 0;
  const lon = viewState.longitude ?? 0;
  const zoom = viewState.zoom ?? 2;
  const bearing = ((viewState.bearing ?? 0) % 360 + 360) % 360;
  const pitch = viewState.pitch ?? 0;

  const alt = formatAltitude(zoomToAltitude(zoom, lat, vh));
  const contacts = aircraft.length + militaryAir.length + ships.length;
  const activeAlerts = crisisAlerts.filter((a) => !a.dismissed).length;

  const zulu =
    `${String(now.getUTCHours()).padStart(2, '0')}:` +
    `${String(now.getUTCMinutes()).padStart(2, '0')}:` +
    `${String(now.getUTCSeconds()).padStart(2, '0')}Z`;

  // 우측 패널 폭만큼 HUD 영역을 좁힌다 (데스크톱)
  const mapArea = 'left-0 right-0 md:right-[42%] lg:right-[38%]';

  return (
    <div className={`absolute top-0 bottom-0 ${mapArea} z-[25] pointer-events-none select-none`}>
      {/* ── 코너 브래킷 ────────────────────────────────── */}
      {([
        ['top-3 left-3', 'M 0 26 L 0 0 L 26 0'],
        ['top-3 right-3 scale-x-[-1]', 'M 0 26 L 0 0 L 26 0'],
        ['bottom-3 left-3 scale-y-[-1]', 'M 0 26 L 0 0 L 26 0'],
        ['bottom-3 right-3 scale-x-[-1] scale-y-[-1]', 'M 0 26 L 0 0 L 26 0'],
      ] as const).map(([cls, d], i) => (
        <svg key={i} className={`absolute ${cls}`} width={26} height={26}>
          <path d={d} fill="none" stroke={accent} strokeWidth={1.5} strokeOpacity={0.5} />
        </svg>
      ))}

      {/* ── 상단 베어링 테이프 ─────────────────────────── */}
      <div className="absolute top-2 left-1/2 -translate-x-1/2 hidden sm:block">
        <BearingTape bearing={bearing} accent={accent} />
        <div className="text-center -mt-0.5">
          <span className="font-mono text-[10px] tabular-nums" style={{ color: accent }}>
            {String(Math.round(bearing)).padStart(3, '0')}°
          </span>
        </div>
      </div>

      {/* ── 중앙 레티클 ────────────────────────────────── */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-70">
        <Reticle accent={accent} />
      </div>

      {/* ── 좌측 고도 리드아웃 ─────────────────────────── */}
      <div className="absolute left-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col items-start gap-1">
        <div className="font-mono text-[8px] tracking-[0.18em] text-slate-500">ALT</div>
        <div className="flex items-baseline gap-1">
          <span className="font-mono text-[18px] tabular-nums leading-none" style={{ color: accent }}>
            {alt.value}
          </span>
          <span className="font-mono text-[9px] text-slate-500">{alt.unit}</span>
        </div>
        {/* 고도 사다리 */}
        <svg width={16} height={120} className="mt-1">
          {Array.from({ length: 13 }, (_, i) => i).map((i) => (
            <line
              key={i}
              x1={0} y1={i * 10} x2={i % 3 === 0 ? 12 : 6} y2={i * 10}
              stroke={accent} strokeWidth={1} strokeOpacity={i % 3 === 0 ? 0.45 : 0.2}
            />
          ))}
          <path
            d={`M 14 ${Math.min(118, Math.max(2, 120 - (zoom / 20) * 120))} l 0 -4 l -6 4 l 6 4 z`}
            fill={accent} fillOpacity={0.9}
          />
        </svg>
        <div className="font-mono text-[8px] tracking-[0.18em] text-slate-500">
          Z{zoom.toFixed(1)} · P{Math.round(pitch)}°
        </div>
      </div>

      {/* ── 하단 리드아웃 바 ───────────────────────────── */}
      <div className="absolute bottom-[72px] md:bottom-20 left-4 md:left-[200px] right-4 flex items-end justify-between gap-4">
        {/* 좌: 좌표 */}
        <div className="flex flex-col gap-1">
          <Readout label="CTR" value={`${toDMS(lat, 'lat')}  ${toDMS(lon, 'lon')}`} accent={accent} />
          <Readout
            label="CUR"
            value={cursorGeo ? `${toDMS(cursorGeo.lat, 'lat')}  ${toDMS(cursorGeo.lon, 'lon')}` : '——  ——'}
            accent={accent}
            dim={!cursorGeo}
          />
        </div>

        {/* 우: 이미저리 출처 — 커맨드 독이 코드만 보여주므로 여기서 귀속표시 */}
        <div className="hidden xl:flex flex-col items-end gap-1">
          <Readout label="MODE" value={sensor.label.toUpperCase()} accent={accent} />
          <span className="font-mono text-[8px] tracking-[0.12em] text-slate-600">{basemap.attribution}</span>
        </div>
      </div>

      {/* ── 좌상단 상태 칩 ─────────────────────────────── */}
      <div className="absolute top-3 left-12 flex items-center gap-2">
        <span
          className="font-mono text-[9px] tracking-[0.2em] px-1.5 py-0.5 border"
          style={{ color: accent, borderColor: `${accent}55`, background: `${accent}12` }}
        >
          {zulu}
        </span>
        <span className="font-mono text-[9px] tracking-[0.2em] text-slate-500">
          CONTACTS <span style={{ color: accent }}>{contacts.toLocaleString()}</span>
        </span>
        <span className="font-mono text-[9px] tracking-[0.2em] text-slate-500 hidden lg:inline">
          EVENTS <span style={{ color: accent }}>{events.length.toLocaleString()}</span>
        </span>
        {activeAlerts > 0 && (
          <span className="font-mono text-[9px] tracking-[0.2em] text-threat-critical animate-pulse">
            ALERT {activeAlerts}
          </span>
        )}
      </div>
    </div>
  );
};
